// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — Show anchor (the button under the playback speed bar): turns this device's screen into the
 * shared-AR marker (`docs/SHARED_AR_PLAN.md`), for a spare phone or
 * tablet laid flat where the sphere should be. Other phones scan it.
 *
 * The marker is drawn as large as the screen allows: the largest square
 * that fits under the one-line header, on white, whatever the theme. No
 * physical size is aimed for, because the scan never uses one — it
 * measures the marker against the surface under it — so "as large as
 * fits" is the right size on every device.
 *
 * The picture comes from the stream host's R2 bucket, like the shared
 * country borders, and falls back to the copy bundled with the site
 * (`public/ayni-xr-anchor.svg`) when the host is not configured or does
 * not answer. The two are the same file: `ANCHOR_MARKER_KEY` carries a
 * version, and a change to the layout in `sharedMarker.ts` means a new
 * upload under the next one.
 *
 * Beside the marker is a QR code that joins a shared session: scanned
 * with a phone's camera it opens the site in the room this anchor stands
 * for, where the first phone to arrive leads and the rest follow
 * (`roomSync.ts`). The anchor's own device does not join; it is furniture.
 * Its room code is kept for the tab's life, so closing and reopening the
 * anchor does not strand the people already in the room.
 *
 * While open it asks for fullscreen, for the screen to stay awake and,
 * once fullscreen, for its orientation to stay as it is, all best-effort:
 * a marker that dims, locks or turns mid-session stops being one.
 *
 * **Print** (owner, 2026-10-22) puts the same two things on a sheet of
 * paper: the marker as large as the page allows (16 cm on A4, well over
 * the 10 cm the scan wants) with the join QR, the room code and the
 * hint under it. It opens in a new tab and asks to print there, as the
 * lesson worksheet and the briefing pack do; a blocked tab saves the
 * sheet as a file instead. Paper needs no battery and never turns.
 */

import { t } from '../i18n'
import { joinedRoomCode } from '../services/roomClient'
import { newRoomCode, normalizeRoomCode } from '../services/roomProtocol'
import { streamHostBase } from '../services/streamHost'
import { escapeHtml } from './domUtils'
import { drawQr } from './qrCode'

/** The marker's key on the stream host. Immutable: bump the version with the layout. */
export const ANCHOR_MARKER_KEY = 'shared/xr/ayni-xr-anchor-v1.svg'
/** The same picture, bundled with the site. */
export const ANCHOR_MARKER_FALLBACK = '/ayni-xr-anchor.svg'

/** Where the marker is fetched from first: the stream host (`streamHost.ts`); the bundled copy for a blank or unparsable host. */
export function anchorMarkerUrl(
  base: string | undefined = streamHostBase(),
): string {
  const root = base?.trim()
  if (!root) return ANCHOR_MARKER_FALLBACK
  try {
    return new URL(ANCHOR_MARKER_KEY, root.endsWith('/') ? root : `${root}/`).toString()
  } catch {
    return ANCHOR_MARKER_FALLBACK
  }
}

interface WakeLockSentinelLike {
  release(): Promise<void>
}

const ROOM_CODE_STORAGE_KEY = 'ayni-anchor-room'

/**
 * The room this anchor stands for: the one this page is already in, else
 * the one this tab's anchor showed before, else a new one.
 */
export function anchorRoomCode(): string {
  const joined = joinedRoomCode()
  if (joined) return joined
  try {
    const kept = normalizeRoomCode(sessionStorage.getItem(ROOM_CODE_STORAGE_KEY))
    if (kept) return kept
  } catch {
    // No session storage (private mode): a code that lasts while the page does.
  }
  const fresh = newRoomCode()
  try {
    sessionStorage.setItem(ROOM_CODE_STORAGE_KEY, fresh)
  } catch {
    // As above.
  }
  return fresh
}

/** The address that joins room `code` on this site. */
export function roomJoinUrl(code: string, origin: string = window.location.origin): string {
  return `${origin}/?room=${code}`
}

/** The parts of the Screen Orientation API used here; not every lib.dom carries `lock`. */
interface OrientationLike {
  readonly type: string
  lock?(orientation: string): Promise<void>
  unlock?(): void
}

function screenOrientation(): OrientationLike | null {
  return (screen as Screen & { orientation?: OrientationLike }).orientation ?? null
}

/** Lock the screen to the way it is turned now; resolves true when it held. */
async function lockOrientation(): Promise<boolean> {
  const orientation = screenOrientation()
  if (!orientation || typeof orientation.lock !== 'function') return false
  await orientation.lock(orientation.type)
  return true
}

function unlockOrientation(): void {
  try {
    screenOrientation()?.unlock?.()
  } catch {
    // Already unlocked, or never lockable.
  }
}

/** What goes on the printed sheet. */
export interface AnchorSheetInput {
  /** The room the sheet joins. */
  code: string
  /** The address the QR code holds. */
  joinUrl: string
  /** The marker picture, as an absolute URL (the sheet is a blob document, so a relative one would not resolve). */
  markerSrc: string
  /** The bundled copy, absolute, tried when `markerSrc` does not load. */
  markerFallback: string
  /** The join QR as an SVG (`qrCode.ts`), or null when it was not drawn: the code is still printed. */
  qrSvg: string | null
  /** The page's language. */
  lang: string
}

/**
 * The anchor as one printable HTML document: the marker, then the QR
 * with the room code and the hint. Scripts-free but for the picture's
 * fallback; nothing is fetched beyond the marker itself.
 */
export function anchorSheetHtml(input: AnchorSheetInput): string {
  const title = t('anchor.title')
  const fallback = escapeHtml(input.markerFallback)
  const onError = input.markerSrc === input.markerFallback
    ? ''
    : ` onerror="this.onerror=null;this.src='${fallback}'"`
  const caption = escapeHtml(t('anchor.join.caption', { code: input.code }))
  return `<!doctype html>
<html lang="${escapeHtml(input.lang)}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>
  body { font: 12pt/1.4 system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; color: #111; background: #fff; margin: 0 auto; max-width: 18cm; padding: 1cm; display: flex; flex-direction: column; align-items: center; gap: 0.8cm; }
  .marker { display: block; inline-size: 16cm; block-size: 16cm; max-inline-size: 100%; aspect-ratio: 1; }
  .join { display: flex; align-items: center; gap: 0.8cm; break-inside: avoid; }
  .qr { inline-size: 5cm; block-size: 5cm; flex: none; }
  .qr svg { display: block; inline-size: 100%; block-size: 100%; }
  .code { margin: 0; font-size: 18pt; font-weight: 700; letter-spacing: 0.06em; }
  .link { margin: 0.2cm 0 0; font-size: 10pt; color: #444; word-break: break-all; }
  .hint { margin: 0; font-size: 10.5pt; color: #333; text-align: center; }
  @media print { body { padding: 0; } @page { size: A4; margin: 1.2cm; } }
</style>
</head>
<body>
  <img class="marker" src="${escapeHtml(input.markerSrc)}" alt="${escapeHtml(t('anchor.alt'))}"${onError}>
  <div class="join">
    ${input.qrSvg ? `<div class="qr" role="img" aria-label="${caption}">${input.qrSvg}</div>` : ''}
    <div>
      <p class="code">${caption}</p>
      <p class="link">${escapeHtml(input.joinUrl)}</p>
    </div>
  </div>
  <p class="hint">${escapeHtml(t('anchor.print.hint'))}</p>
</body>
</html>`
}

/**
 * Open the sheet in a new tab and ask to print it there (or save it as
 * PDF); a blocked tab saves it as a file. Synchronous, so the click's
 * gesture is still fresh when the tab is asked for.
 */
export function printAnchorSheet(input: AnchorSheetInput): void {
  const blob = new Blob([anchorSheetHtml(input)], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  const tab = window.open(url, '_blank')
  if (tab) {
    tab.addEventListener('load', () => {
      try {
        tab.focus()
        tab.print()
      } catch {
        /* the tab is open; printing is the reader's */
      }
    }, { once: true })
  } else {
    const a = document.createElement('a')
    a.href = url
    a.download = `ayni-xr-anchor-${input.code}.html`
    document.body.appendChild(a)
    a.click()
    a.remove()
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

let closeOpenPanel: (() => void) | null = null

/**
 * Wire `#anchor-btn`. The panel opens straight from the click, because
 * fullscreen needs the user's gesture.
 */
export function initAnchorButton(): void {
  const button = document.getElementById('anchor-btn')
  if (!button || button.dataset.wired === 'true') return
  button.dataset.wired = 'true'
  button.addEventListener('click', () => openAnchorPanel(button))
}

/** True while the anchor is on screen. */
export function isAnchorPanelOpen(): boolean {
  return closeOpenPanel !== null
}

/** Close the anchor if it is open. */
export function closeAnchorPanel(): void {
  closeOpenPanel?.()
}

/**
 * Show the anchor over the whole page. `trigger` gets the focus back
 * when it closes; `source` is where the picture is tried first. Opening
 * it twice is a no-op.
 */
export function openAnchorPanel(
  trigger?: HTMLElement | null,
  source: string = anchorMarkerUrl(),
): void {
  if (closeOpenPanel) return

  const panel = document.createElement('div')
  panel.id = 'anchor-panel'
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-modal', 'true')
  panel.setAttribute('aria-label', t('anchor.title'))

  const header = document.createElement('div')
  header.className = 'anchor-panel-header'
  const hint = document.createElement('p')
  hint.className = 'anchor-panel-hint'
  hint.textContent = t('anchor.hint')
  const print = document.createElement('button')
  print.type = 'button'
  print.className = 'anchor-panel-print'
  print.setAttribute('aria-label', t('anchor.print.aria'))
  print.textContent = t('anchor.print')
  const close = document.createElement('button')
  close.type = 'button'
  close.className = 'anchor-panel-close'
  close.setAttribute('aria-label', t('anchor.close.aria'))
  close.textContent = '✕'
  header.append(hint, print, close)

  const image = document.createElement('img')
  image.className = 'anchor-panel-marker'
  image.alt = t('anchor.alt')
  image.draggable = false
  const onImageError = (): void => {
    // The stream host did not answer: the bundled copy is the same file.
    image.removeEventListener('error', onImageError)
    if (source !== ANCHOR_MARKER_FALLBACK) image.src = ANCHOR_MARKER_FALLBACK
  }
  image.addEventListener('error', onImageError)
  image.src = source

  // The way in for everyone else: a QR code for this anchor's room.
  const code = anchorRoomCode()
  const join = document.createElement('div')
  join.className = 'anchor-panel-join'
  const qrHost = document.createElement('div')
  qrHost.className = 'anchor-panel-qr'
  const caption = document.createElement('p')
  caption.className = 'anchor-panel-join-caption'
  caption.textContent = t('anchor.join.caption', { code })
  join.append(qrHost, caption)
  void drawQr(qrHost, roomJoinUrl(code)).catch(() => {
    // Without the encoder the code in the caption is still the way in.
    qrHost.remove()
  })

  // The same marker and code on paper. The QR already drawn on screen is
  // reused, so nothing is awaited between the click and the new tab.
  print.addEventListener('click', () => {
    printAnchorSheet({
      code,
      joinUrl: roomJoinUrl(code),
      markerSrc: image.src,
      markerFallback: new URL(ANCHOR_MARKER_FALLBACK, window.location.origin).toString(),
      qrSvg: qrHost.querySelector('svg')?.outerHTML ?? null,
      lang: document.documentElement.lang || 'en',
    })
  })

  const body = document.createElement('div')
  body.className = 'anchor-panel-body'
  body.append(image, join)
  panel.append(header, body)
  document.body.appendChild(panel)

  // Keep the screen on. The lock is dropped by the browser whenever the
  // page is hidden, so it is asked for again each time the page returns.
  let wakeLock: WakeLockSentinelLike | null = null
  let closed = false
  const requestWakeLock = (): void => {
    const api = (navigator as Navigator & {
      wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> }
    }).wakeLock
    if (!api || document.visibilityState !== 'visible') return
    api.request('screen').then(
      (lock) => {
        if (closed) void lock.release().catch(() => { /* already released */ })
        else wakeLock = lock
      },
      () => { /* refused (battery saver, no permission): the hint still says to keep it on */ },
    )
  }
  const onVisibility = (): void => {
    if (document.visibilityState === 'visible') requestWakeLock()
  }
  document.addEventListener('visibilitychange', onVisibility)
  requestWakeLock()

  // Fullscreen takes the browser's bars away, which is most of what
  // "as large as the screen allows" means on a phone. Refused without a
  // user gesture, or on iPhone: the panel still covers the page.
  //
  // Once fullscreen, the screen's orientation is locked as it stands. A
  // tablet laid flat flips between portrait and landscape at a nudge, and
  // each flip turns the marker and moves it (the layout changes with the
  // orientation): everyone who scanned before the flip would then disagree
  // with everyone after it. Browsers allow the lock only in fullscreen.
  let enteredFullscreen = false
  let lockedOrientation = false
  if (!document.fullscreenElement && typeof panel.requestFullscreen === 'function') {
    panel.requestFullscreen().then(
      () => {
        enteredFullscreen = true
        if (closed) return
        lockOrientation().then(
          (locked) => {
            if (!locked) return
            if (closed) unlockOrientation()
            else lockedOrientation = true
          },
          () => { /* not lockable: the hint still says to leave it be */ },
        )
      },
      () => { /* not allowed here */ },
    )
  }
  // Leaving fullscreen by the system's own gesture (back, Escape) closes
  // the anchor too: on a phone that gesture is the natural "done".
  const onFullscreenChange = (): void => {
    if (enteredFullscreen && !document.fullscreenElement) doClose()
  }
  document.addEventListener('fullscreenchange', onFullscreenChange)

  const onKeyDown = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') {
      ev.preventDefault()
      doClose()
    } else if (ev.key === 'Tab') {
      // Two controls: the focus goes between them and nowhere else.
      ev.preventDefault()
      if (document.activeElement === print) close.focus()
      else print.focus()
    }
  }
  panel.addEventListener('keydown', onKeyDown)

  function doClose(): void {
    if (closed) return
    closed = true
    closeOpenPanel = null
    document.removeEventListener('visibilitychange', onVisibility)
    document.removeEventListener('fullscreenchange', onFullscreenChange)
    void wakeLock?.release().catch(() => { /* already released */ })
    wakeLock = null
    if (lockedOrientation) unlockOrientation()
    if (enteredFullscreen && document.fullscreenElement === panel) {
      void document.exitFullscreen().catch(() => { /* already out */ })
    }
    panel.remove()
    trigger?.focus()
  }
  close.addEventListener('click', doClose)
  closeOpenPanel = doClose
  close.focus()
}
